// src/pages/Landing.js
// Public marketing homepage — hero, scrolling strip, feature rows, model grid,
// bento tool grid, FAQ, closing CTA. Shown to logged-out visitors;
// "Start Creating" / "Get Started" open signup.
//
// Photography is served from `public/landing/` (see data/landingMedia.js) —
// the provider credit line at the foot of this page is required by the Pexels
// and Pixabay API terms and should not be removed while those images are used.

import { useEffect, useState } from "react";
import { models as modelsApi } from "../api";
import { img, PROVIDERS } from "../data/landingMedia";
import useReveal from "../lib/useReveal";

const STRIP = [
  "Character Chat", "Story Video", "Product Ad", "Music Video", "Anime Style",
  "Cinematic Trailer", "UGC Ad", "Talking Avatar", "Brand Content",
];

/* ---------- inline icons (stroke, 24px grid) ---------- */
const Icon = ({ d, children, size = 14 }) => (
  <svg
    className="ico"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {children || <path d={d} />}
  </svg>
);

const IconUser = (p) => (
  <Icon {...p}><circle cx="12" cy="8" r="4" /><path d="M5 21c0-3.9 3.1-7 7-7s7 3.1 7 7" /></Icon>
);
const IconImage = (p) => (
  <Icon {...p}><rect x="3" y="4" width="18" height="16" rx="3" /><circle cx="8.5" cy="9.5" r="1.6" /><path d="m4 17 5-5 4 4 3-2 4 4" /></Icon>
);
const IconFilm = (p) => (
  <Icon {...p}><rect x="3" y="5" width="18" height="14" rx="3" /><path d="M3 10h18" /><path d="M8 5v5" /><path d="M16 5v5" /></Icon>
);
const IconRemix = (p) => (
  <Icon {...p}><path d="M3 12a9 9 0 0 1 15.5-6.2L21 8" /><path d="M21 3v5h-5" /><path d="M21 12a9 9 0 0 1-15.5 6.2L3 16" /><path d="M3 21v-5h5" /></Icon>
);
const IconPlay = (p) => <Icon {...p}><polygon points="6 3 20 12 6 21 6 3" /></Icon>;
const IconRefX = (p) => (
  <Icon {...p}><rect x="3" y="3" width="8" height="8" rx="2" /><rect x="13" y="13" width="8" height="8" rx="2" /><path d="M11 7h4a2 2 0 0 1 2 2v4" /></Icon>
);
const IconBag = (p) => (
  <Icon {...p}><path d="M6 3h12l2 5H4z" /><path d="M5 8v11a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8" /><path d="M9 12h6" /></Icon>
);
const IconShots = (p) => (
  <Icon {...p}><rect x="2" y="6" width="13" height="12" rx="2" /><path d="m15 10 7-4v12l-7-4z" /></Icon>
);
const IconBrain = (p) => (
  <Icon {...p}><circle cx="12" cy="6" r="3" /><circle cx="5" cy="18" r="3" /><circle cx="19" cy="18" r="3" /><path d="M12 9v3" /><path d="m10 13-3 2" /><path d="m14 13 3 2" /></Icon>
);
const IconMic = (p) => (
  <Icon {...p}><rect x="9" y="3" width="6" height="11" rx="3" /><path d="M5 11a7 7 0 0 0 14 0" /><path d="M12 18v3" /></Icon>
);
const IconSpark = (p) => (
  <Icon {...p}><path d="m12 3 1.9 5.6L19.5 10l-5.6 1.9L12 17.5l-1.9-5.6L4.5 10l5.6-1.4z" /></Icon>
);
const IconHeart = (p) => (
  <Icon {...p}><path d="M20.8 6.6a5 5 0 0 0-7.1 0L12 8.3l-1.7-1.7a5 5 0 1 0-7.1 7.1l8.8 8.8 8.8-8.8a5 5 0 0 0 0-7.1z" /></Icon>
);
const IconChat = (p) => (
  <Icon {...p}><path d="M21 11.5a8.4 8.4 0 0 1-9 8.5 9 9 0 0 1-4.1-.9L3 21l1.9-4.6A8.4 8.4 0 0 1 12 3a8.4 8.4 0 0 1 9 8.5z" /></Icon>
);

/* ---------- content ---------- */
const FEATURES = [
  {
    tag: "Characters",
    Ico: IconUser,
    art: "feat-character",
    overlay: { kind: "quote", text: "You still owe me that coffee from Lisbon." },
    title: "Build a character once, keep them forever",
    body: "Create a persona with a personality, a backstory and a memory that actually persists. The Memory Brain tracks every relationship, scene and detail across every conversation, so your character never forgets.",
    cta: "Start chatting",
    route: "characters",
  },
  {
    tag: "Image Studio",
    Ico: IconImage,
    art: "feat-image",
    overlay: { kind: "prompt", text: "liquid chrome portrait, magenta rim light" },
    chips: ["Text to image", "RefX", "PixSync ads"],
    title: "Generate, restyle and remix any image",
    body: "Text-to-image, reference-guided RefX and PixSync brand ads live on one canvas. Swap a background, restyle a shot or upscale it — with your subject's likeness held steady across every edit.",
    cta: "Create an image",
    route: "image",
  },
  {
    tag: "Video Studio",
    Ico: IconFilm,
    art: "feat-video",
    overlay: { kind: "shots" },
    title: "Direct cinematic video from a single prompt",
    body: "Turn one idea into a multi-shot sequence with the AI director, or animate a reference image straight into motion. Lip-synced speech, music and sound are rendered inside Karvi — no second tool, no export dance.",
    cta: "Create a video",
    route: "video",
  },
  {
    tag: "Drift",
    Ico: IconRemix,
    art: "drift",
    title: "A feed built for remixing, not just scrolling",
    body: "Every post on Drift carries its full recipe. Tap Remix on anything you like and it opens pre-filled in the right studio — model, prompt, references and all. The original creator earns credits every time.",
    cta: "Explore Drift",
    route: "drift",
  },
];

const MODELS = [
  { name: "Seedance 2.0", blurb: "Fluid motion, lifelike physics and cinematic camera work.", kind: "Video", route: "video", art: "m-1" },
  { name: "Veo 3.1 Fast", blurb: "Cinematic video with native audio and film-grade quality.", kind: "Video", route: "video", art: "m-2" },
  { name: "Kling 3.0 Pro", blurb: "Stylised motion, anime sequences, multi-shot continuity.", kind: "Video", route: "video", art: "m-3" },
  { name: "Seedream 4.5", blurb: "Photoreal stills with sharp detail and tight prompt adherence.", kind: "Image", route: "image", art: "m-4" },
  { name: "GPT Image 2", blurb: "Excels at typography, infographics and precise instructions.", kind: "Image", route: "image", art: "m-5" },
  { name: "Nano Banana Pro", blurb: "Precise edits and character consistency in plain language.", kind: "Image", route: "image", art: "m-6" },
  { name: "Wan 2.5", blurb: "Fast, expressive image-to-video animation.", kind: "Video", route: "video", art: "m-7" },
  { name: "Flux Pro Kontext", blurb: "Reference-guided generation that holds a subject's likeness.", kind: "Image", route: "image", art: "m-8" },
];

// Bento: `span` is the class suffix driving grid placement (see App.css).
const TOOLS = [
  { span: "tall", Ico: IconShots, art: "feat-video", title: "Multi-Shot Director", body: "One prompt becomes a full sequence of cinematic, varied shots — framing, camera move and continuity handled for you." },
  { span: "wide", Ico: IconRefX, title: "RefX", body: "Guide any generation with a reference image while keeping full creative control." },
  { span: "unit", Ico: IconBag, title: "PixSync", body: "Turn a product photo into on-brand ad creative." },
  { span: "unit", Ico: IconBrain, title: "Memory Brain", body: "A living graph of everything your character remembers." },
  { span: "wide", Ico: IconRemix, title: "Remix", body: "Open any Drift post pre-filled in the studio and make it your own." },
  { span: "wide", Ico: IconMic, title: "Talking Avatar", body: "Give any character a lip-synced voice in 30+ languages." },
];

const FAQS = [
  { q: "What is Karvi AI?", a: "Karvi is an all-in-one platform for AI characters, images and video. Chat with a persistent character, generate and edit images, direct cinematic video, and share your best work on Drift — all in one place." },
  { q: "Do I need any design or editing experience?", a: "No. Describe what you want in plain language, pick a style, and Karvi handles the rest. Advanced controls (RefX, multi-shot, camera angles) are there when you want more precision." },
  { q: "Can I use what I create commercially?", a: "Yes — you own what you generate and can use it in ads, social content, or client work." },
  { q: "What makes Drift different from a normal feed?", a: "Every post carries its full generation recipe. Remix any post to open it pre-filled in the matching studio, and the original creator earns credits when you do." },
  { q: "Is Karvi free to start?", a: "Yes, new accounts start with free daily credits — no card required. Paid plans unlock higher-tier models, longer videos, and more credits." },
];

export default function Landing({ onNavigate, onSignup }) {
  const [openFaq, setOpenFaq] = useState(0);
  const [thumbs, setThumbs] = useState({}); // display_name -> thumbnail_url

  // Pull real listing thumbnails so the model grid shows live artwork; the
  // bundled stills are the fallback when a listing has no thumbnail yet.
  useEffect(() => {
    Promise.all([modelsApi.image(), modelsApi.video()])
      .then(([image, video]) => {
        const map = {};
        [...(image.models || []), ...(video.models || [])].forEach((m) => {
          if (m.thumbnail_url) map[m.display_name] = m.thumbnail_url;
        });
        setThumbs(map);
      })
      .catch(() => {});
  }, []);

  useReveal([]);

  const go = (route) => (route === "signup" ? onSignup() : onNavigate(route));

  return (
    <div className="site">

      {/* ---------- Hero ---------- */}
      <section className="hero">
        <div className="hero__bg" style={{ backgroundImage: `url(${img("hero-main")})` }} />
        <div className="hero__scrim" />

        <div className="hero__inner">
          <div className="hero__copy">
            <span className="hero__eyebrow"><i className="hero__dot" />CHARACTERS · IMAGES · VIDEO</span>
            <h1 className="hero__title">
              Where ideas<br />
              <span className="grad-text">become visual stories</span>
            </h1>
            <p className="hero__sub">
              Characters that remember every conversation, images you can restyle endlessly,
              and cinematic video directed from a single prompt — all in one place.
            </p>
            <div className="hero__cta">
              <button className="btn btn--primary btn--lg" onClick={() => go("signup")}>Start creating</button>
              <button className="btn btn--ghost btn--lg btn--icon" onClick={() => go("drift")}>
                <IconPlay size={15} />Explore Drift
              </button>
            </div>
            <span className="hero__note">Free daily credits · No card required</span>
          </div>

          <div className="hero__art" aria-hidden="true">
            <div className="hero__col hero__col--offset">
              <figure className="shot shot--sm">
                <img src={img("hero-a")} alt="" loading="lazy" />
                <figcaption>CHARACTER</figcaption>
              </figure>
              <figure className="shot shot--xs">
                <img src={img("hero-c")} alt="" loading="lazy" />
                <figcaption>RESTYLE</figcaption>
              </figure>
            </div>
            <div className="hero__col">
              <figure className="shot shot--lg">
                <img src={img("hero-b")} alt="" loading="lazy" />
                <figcaption>IMAGE STUDIO</figcaption>
              </figure>
              <div className="hero__chip">
                <span className="hero__chipIcon"><IconBrain size={17} /></span>
                <span>
                  <b>Memory Brain</b>
                  <em>Nothing your character forgets</em>
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- Scrolling strip ---------- */}
      <div className="strip">
        <div className="strip__track">
          {[...STRIP, ...STRIP].map((s, i) => (
            <span className="strip__pill" key={i}>{s}</span>
          ))}
        </div>
      </div>

      {/* ---------- Feature rows ---------- */}
      <section className="feat-section">
        <div className="site__head reveal">
          <span className="site__eyebrow">ONE PLATFORM. INFINITE STORIES.</span>
          <h2 className="site__h2">Every tool you need, without leaving Karvi</h2>
        </div>

        <div className="feat-rows">
          {FEATURES.map((f, i) => (
            <div className={`feat-row ${i % 2 ? "feat-row--rev" : ""} reveal`} key={f.tag}>
              <div className="feat-row__art">
                {f.art === "drift" ? (
                  <div className="drift-stack">
                    <figure className="drift-stack__side"><img src={img("drift-2")} alt="" loading="lazy" /></figure>
                    <figure className="drift-stack__main">
                      <img src={img("feat-drift")} alt="" loading="lazy" />
                      <span className="drift-stack__rail">
                        <IconHeart size={20} /><IconChat size={20} /><IconRemix size={20} />
                      </span>
                      <figcaption>
                        <b>@noor.builds</b>
                        <em>Seedance 2.0 · remixed 214×</em>
                      </figcaption>
                    </figure>
                    <figure className="drift-stack__side"><img src={img("drift-3")} alt="" loading="lazy" /></figure>
                  </div>
                ) : (
                  <figure className="frame">
                    <img src={img(f.art)} alt="" loading="lazy" />
                    <span className="frame__fade" />
                    {f.overlay?.kind === "quote" && (
                      <span className="frame__bar"><i className="dot dot--live" />{`“${f.overlay.text}”`}</span>
                    )}
                    {f.overlay?.kind === "prompt" && (
                      <span className="frame__bar frame__bar--dim"><IconSpark size={15} />{f.overlay.text}</span>
                    )}
                    {f.overlay?.kind === "shots" && (
                      <span className="frame__shots">
                        <b>SHOT 1</b><i>SHOT 2</i><i>SHOT 3</i><u>00:12</u>
                      </span>
                    )}
                  </figure>
                )}
              </div>

              <div className="feat-row__body">
                <span className="feat-row__tag"><f.Ico />{f.tag}</span>
                <h3>{f.title}</h3>
                <p>{f.body}</p>
                {f.chips && (
                  <div className="chips">
                    {f.chips.map((c) => <span className="chip" key={c}>{c}</span>)}
                  </div>
                )}
                <button className="btn btn--primary" onClick={() => go(f.route)}>{f.cta} →</button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ---------- Model grid ---------- */}
      <section className="site-section">
        <div className="site__head reveal">
          <span className="site__eyebrow">TOP AI MODELS. ONE SUBSCRIPTION.</span>
          <h2 className="site__h2">Explore leading models for image &amp; video</h2>
        </div>
        <div className="model-grid reveal">
          {MODELS.map((m) => (
            <button className="model-card" key={m.name} onClick={() => go(m.route)}>
              <span
                className="model-card__thumb"
                style={{ backgroundImage: `url(${thumbs[m.name] || img(m.art)})` }}
              />
              <span className="model-card__body">
                <h4>{m.name}</h4>
                <p>{m.blurb}</p>
                <span className="model-card__kind">{m.kind}</span>
              </span>
            </button>
          ))}
        </div>
      </section>

      {/* ---------- Tools (bento) ---------- */}
      <section className="site-section">
        <div className="site__head reveal">
          <span className="site__eyebrow">STUDIO-GRADE CONTROLS</span>
          <h2 className="site__h2">Purpose-built tools, not just text-to-pixel</h2>
        </div>
        <div className="bento reveal">
          {TOOLS.map((t) => (
            <div className={`bento__cell bento__cell--${t.span}`} key={t.title}>
              {t.art && (
                <span className="bento__art" style={{ backgroundImage: `url(${img(t.art)})` }} />
              )}
              <span className="bento__icon"><t.Ico size={19} /></span>
              <h4>{t.title}</h4>
              <p>{t.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ---------- FAQ ---------- */}
      <section className="site-section site-section--narrow">
        <div className="site__head reveal">
          <span className="site__eyebrow">FAQ</span>
          <h2 className="site__h2">Frequently asked questions</h2>
        </div>
        <div className="faq reveal">
          {FAQS.map((f, i) => (
            <div className={`faq__item ${openFaq === i ? "faq__item--open" : ""}`} key={f.q}>
              <button
                className="faq__q"
                aria-expanded={openFaq === i}
                onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
              >
                {f.q}
                <span className="faq__chev">{openFaq === i ? "−" : "+"}</span>
              </button>
              {openFaq === i && <p className="faq__a">{f.a}</p>}
            </div>
          ))}
        </div>
      </section>

      {/* ---------- Closing CTA ---------- */}
      <section className="cta-band reveal">
        <div className="cta-band__bg" style={{ backgroundImage: `url(${img("x-cta")})` }} />
        <div className="cta-band__scrim" />
        <div className="cta-band__inner">
          <h2>Create without limits</h2>
          <p>Generate, refine and share — your first character, image or film is a prompt away.</p>
          <button className="btn btn--primary btn--lg" onClick={() => go("signup")}>Start creating</button>
        </div>
      </section>

      {/* Provider credit — required by the Pexels and Pixabay API terms
          while their photography is displayed on this page. */}
      <p className="site__credit">
        Photography via{" "}
        {PROVIDERS.map((p, i) => (
          <span key={p.name}>
            {i > 0 && " and "}
            <a href={p.url} target="_blank" rel="noreferrer noopener">{p.name}</a>
          </span>
        ))}
      </p>
    </div>
  );
}
